import type { Language } from '@/apps/provider/profile/__types__/SkillsTypes';
import { EducationFormState } from './CareTypeFormOptions';
import HelperStatus from '@/helpers/status';

export type PetCareQualification =
  | 'DOG_FIRST_AID_TRAINING'
  | 'DOG_CPR_TRAINING'
  | 'CAN_ADMINISTER_ORAL_MEDICATION'
  | 'CAN_ADMINISTER_INJECTED_MEDICATION'
  | 'DOG_CPR'
  | 'DOG_FIRST_AID'
  | 'ORAL_MEDICATION'
  | 'INJECTED_MEDICATION';

export type PetCareService =
  | 'CHECK_IN'
  | 'WALKING_HALF_HOUR'
  | 'WALKING_HOUR'
  | 'OWNERS_HOME_OVERNIGHT'
  | 'CARERS_HOME_DAYCARE'
  | 'CARERS_HOME_OVERNIGHT'
  | 'TRANSPORT'
  | 'GROOMING'
  | 'TRAINING';

export type PetCareExperience =
  | 'DOGS'
  | 'CATS'
  | 'BIRDS'
  | 'FISH'
  | 'HORSES'
  | 'FARM_ANIMALS'
  | 'REPTILES_AMPHIBIANS'
  | 'EXOTIC_PETS'
  | 'SMALL_MAMMALS'
  | 'OTHER';

export const PetCareExperienceOptions = [
  'DOGS',
  'CATS',
  'BIRDS',
  'FISH',
  'HORSES',
  'FARM_ANIMALS',
  'REPTILES_AMPHIBIANS',
  'EXOTIC_PETS',
  'SMALL_MAMMALS',
  'OTHER',
];

export const serviceRateToServiceKey = {
  visitRate: 'CHECK_IN',
  thirtyMinuteWalkRate: 'WALKING_HALF_HOUR',
  sixtyMinuteWalkRate: 'WALKING_HOUR',
  overnightRateOwnerHome: 'OWNERS_HOME_OVERNIGHT',
  dayRate: 'CARERS_HOME_DAYCARE',
  overnightRateSitterHome: 'CARERS_HOME_OVERNIGHT',
  transportationRate: 'TRANSPORT',
  ratePerService: 'GROOMING',
  trainingRate: 'TRAINING',
};

enum PetServices {
  CHECK_IN = 'CHECK_IN',
  WALKING_HALF_HOUR = 'WALKING_HALF_HOUR',
  WALKING_HOUR = 'WALKING_HOUR',
  CARERS_HOME_DAYCARE = 'CARERS_HOME_DAYCARE',
  CARERS_HOME_OVERNIGHT = 'CARERS_HOME_OVERNIGHT',
  OWNERS_HOME_OVERNIGHT = 'OWNERS_HOME_OVERNIGHT',
  TRANSPORT = 'TRANSPORT',
  GROOMING = 'GROOMING',
  TRAINING = 'TRAINING',
}

export interface PetCareServiceInput {
  rate: number;
  service: PetCareService;
}
export interface PetCareProfileFormState {
  bio: string;
  distanceWillingToTravel: number;
  education: EducationFormState;
  experience: PetCareExperience[];
  experienceOther: string;
  languagesSpoken: Language[];
  maxNumberOfPets: number;
  qualifications: PetCareQualification[];
  serviceRates: PetCareServiceInput[];
  yearsExperience: number | null;
}

export const petServicesOptions = {
  [PetServices.CHECK_IN]: {
    checkboxLabel: 'Check-ins/visits- per visit',
    inputLabel: '$ per visit',
    service: 'visitRate',
    helperText: 'Enter a check-in and visit rate',
    rate: 0,
    selected: false,
    status: HelperStatus.Neutral,
  },
  [PetServices.WALKING_HALF_HOUR]: {
    checkboxLabel: 'Dog walking- 30-minute walk',
    inputLabel: '$ per 30-minute walk',
    service: 'thirtyMinuteWalkRate',
    helperText: 'Enter a 30-minute dog walk rate',
    rate: 0,
    selected: false,
    status: HelperStatus.Neutral,
  },
  [PetServices.WALKING_HOUR]: {
    checkboxLabel: 'Dog walking- 60-minute walk',
    inputLabel: '$ per 60-minute walk',
    service: 'sixtyMinuteWalkRate',
    helperText: 'Enter a 60-minute dog walk rate',
    rate: 0,
    selected: false,
    status: HelperStatus.Neutral,
  },
  [PetServices.CARERS_HOME_DAYCARE]: {
    checkboxLabel: 'Pet care in my home- per day',
    inputLabel: '$ per day',
    service: 'dayRate',
    helperText: 'Enter a per day pet care rate',
    rate: 0,
    selected: false,
    status: HelperStatus.Neutral,
  },
  [PetServices.CARERS_HOME_OVERNIGHT]: {
    checkboxLabel: 'Overnight pet care in my home- per night',
    inputLabel: '$ per night',
    service: 'overnightRateSitterHome',
    helperText: 'Enter a per night overnight pet care rate',
    rate: 0,
    selected: false,
    status: HelperStatus.Neutral,
  },
  [PetServices.OWNERS_HOME_OVERNIGHT]: {
    checkboxLabel: `Overnight pet care in owner's home- per night`,
    inputLabel: '$ per night',
    service: 'overnightRateOwnerHome',
    helperText: 'Enter a per night overnight pet care rate',
    rate: 0,
    selected: false,
    status: HelperStatus.Neutral,
  },
  [PetServices.TRANSPORT]: {
    checkboxLabel: 'Pick up and drop off- rate each way',
    inputLabel: '$ each way',
    service: 'transportationRate',
    helperText: 'Enter a pick up and drop off rate',
    rate: 0,
    selected: false,
    status: HelperStatus.Neutral,
  },
  [PetServices.GROOMING]: {
    checkboxLabel: 'Bathing and grooming- per service',
    inputLabel: '$ per service',
    service: 'ratePerService',
    helperText: 'Enter a bathing and grooming rate',
    rate: 0,
    selected: false,
    status: HelperStatus.Neutral,
  },
  [PetServices.TRAINING]: {
    checkboxLabel: 'Training- per hour',
    inputLabel: '$ per hour',
    service: 'trainingRate',
    helperText: 'Enter a training hourly rate',
    rate: 0,
    selected: false,
    status: HelperStatus.Neutral,
  },
};

export type PetCareProfileSectionPartial = Partial<PetCareProfileFormState>;
